/** What the record page knows about where its values were read from - P15 item 1.
 *
 * Two reads the server already answers, combined into one question per value: `GET
 * /evidence/{id}/text` says *where* (and re-checks every position before saying it), and `GET
 * /evidence/{id}/review` says *what it was read as and how sure*. `fieldFor` answers "which field, if
 * any, can this value be pointed at", and **nothing here finds a position**: a value with no confirmed
 * one has no field, and the screen draws it as plain text (ADR-0045 refuses relocation).
 */

import { useDocumentText, useReview } from "../api/evidence";
import type { DocumentText, ReviewItem } from "../api/evidence";
import { unitLabel } from "../lib/format";
import { drawn, fieldLabel } from "./fields";
import type { FieldDetail } from "./SourceText";

/** Which fields on a document answer which value on the record page, in the order they are tried.
 * The extraction schemas name the quantity differently per kind of document. */
export const VALUE_FIELDS = {
  quantity: ["consumption_kwh", "quantity", "charge_added_kg", "volume_mwh"],
  period: ["period_start", "period_end"],
  carrier: ["fuel_type", "refrigerant_type", "supplier"],
  meter: ["meter_reference"],
} as const;

export type ValueKind = keyof typeof VALUE_FIELDS;

/** How sure the reader was, in words. The review's own convention (`Review.tsx`): a percentage of the
 * model's score, and a person's correction is not a score at all. */
export function confidenceWords(item: ReviewItem | undefined): string {
  if (!item) return "";
  if (item.corrected) return "corrected by a person";
  if (item.missing) return "";
  const score = Number(item.confidence);
  return Number.isFinite(score) ? `${String(Math.round(score * 100))}%` : "";
}

export interface SourceFields {
  /** The document this record rests on, if any. */
  documentId: string | undefined;
  text: DocumentText | null | undefined;
  /** The field this kind of value was read from, when a position is confirmed; otherwise `null`. */
  fieldFor: (kind: ValueKind) => string | null;
  /** What each field was read as, for the hover and the grid. */
  details: Readonly<Record<string, FieldDetail>>;
  items: readonly ReviewItem[];
  /** The review could not be read, so values and confidence are unknown - not absent. */
  reviewFailed: boolean;
  /** The server said nobody has read this document, so no read was asked for. */
  unread: boolean;
  /** The field currently shown on the bill, held by the page so every part agrees. */
  focus: string | null;
  show: (field: string | null) => void;
}

const NONE: SourceFields = {
  documentId: undefined,
  text: undefined,
  fieldFor: () => null,
  details: {},
  items: [],
  reviewFailed: false,
  unread: false,
  focus: null,
  show: () => undefined,
};

export function useSourceFields(
  documentId: string | undefined,
  focus: string | null,
  show: (field: string | null) => void,
  /** What the server said about the document: `false` is a document nobody has read, which has no
   * text and no review to ask for (each is a 404 by design), so neither is requested. `null` or
   * `undefined` is *not said*, and the reads are made as they always were. */
  read?: boolean | null,
): SourceFields {
  const unread = read === false;
  const textRead = useDocumentText(documentId, Boolean(documentId) && !unread);
  const reviewRead = useReview(unread ? undefined : documentId);
  if (!documentId) return NONE;
  const text = unread ? null : textRead.data;
  const items = reviewRead.data?.items ?? [];
  const byName = new Map(items.map((item) => [item.name, item] as const));
  const details: Record<string, FieldDetail> = {};
  for (const span of text?.spans ?? []) {
    const item = byName.get(span.field);
    details[span.field] = {
      label: fieldLabel(span.field),
      value: item && !item.missing ? `${item.value}${item.unit ? ` ${unitLabel(item.unit)}` : ""}` : "",
      confidence: confidenceWords(item),
    };
  }
  const fieldFor = (kind: ValueKind): string | null => {
    for (const name of VALUE_FIELDS[kind]) {
      if (drawn(text?.spans.find((span) => span.field === name)) !== null) return name;
    }
    return null;
  };
  // A review that failed is said, not read as a document with nothing in it (the values and the
  // confidence are what it carries, and a position needs neither).
  return { documentId, text, fieldFor, details, items, reviewFailed: Boolean(reviewRead.error), unread, focus, show };
}
